import React, { useEffect, useMemo } from 'react';
import useStyles from './styles';
import { PostBody, PostHeader, TagGrid, CreditGrid } from 'molecules';
import Grid2 from '@mui/material/Grid2';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import { Post as PostInfo } from 'types/post';
import {
  handleInstagramEmbeds,
  handleTwitterEmbeds,
  unmountSocialMediaScripts,
} from './Embeds.util';
import clsx from 'clsx';
import { Skeleton } from '@mui/material';
import { useRouter } from 'next/router';

/**
 * Post Component
 *
 * Displays a full post/article with header, body content, tags, credits, and
 * interactive elements. Handles Instagram and Twitter embeds, social media scripts,
 * and provides loading skeleton states. Manages embed lifecycle and routing.
 *
 * Features:
 * - Semantic `<article>` around header, body, and footer (tags/credits)
 * - Post header with author, date, title
 * - Post body with formatted content
 * - Tag display and navigation
 * - Credits grid display
 * - Instagram and Twitter embed handling
 * - Loading skeleton states
 * - Social media script lifecycle management
 *
 * Used in:
 * - Post detail pages
 * - Submission review flow
 *
 * @param props - Component props
 * @param props.content - Post body content (HTML/markdown)
 * @param props.tags - Array of post tags
 * @param props.hasInstagramEmbed - Boolean indicating if post contains Instagram embeds
 * @param props.hasTwitterEmbed - Boolean indicating if post contains Twitter embeds
 * @param props.submission - Submission information if applicable
 * @param props.credits - Array of credits to display
 * @param props.isLoading - Boolean indicating if post is loading
 * @param props - Additional props passed to PostHeader (author, date, title, etc.)
 */
const Post = (props: PostPropTypes) => {
  const { classes } = useStyles();

  const {
    content,
    tags,
    hasInstagramEmbed,
    hasTwitterEmbed,
    submission,
    credits,
    isLoading,
    ...headerProps
  } = props;

  const router = useRouter();

  const isFeature = headerProps.type === 'Featured';

  useEffect(() => {
    if (hasInstagramEmbed) {
      handleInstagramEmbeds();
    }

    if (hasTwitterEmbed) {
      handleTwitterEmbeds();
    }

    return () => {
      unmountSocialMediaScripts();
    };
  }, [hasInstagramEmbed, hasTwitterEmbed]);

  const renderSkeleton = router.isFallback || isLoading;

  const headerClassName = useMemo(
    () =>
      clsx(classes.headerRoot, {
        [classes.featureHeaderRoot]: isFeature,
      }),
    [classes.headerRoot, classes.featureHeaderRoot, isFeature],
  );

  return (
    <Paper className={classes.paperRoot} data-component="Post" elevation={1}>
      <article className={classes.article}>
        <header className={headerClassName}>
          <PostHeader {...headerProps} submission={submission} isLoading={isLoading} />
          <Divider variant={'fullWidth'} className={classes.separator} />
        </header>
        <div className={clsx(classes.root, { [classes.featureRoot]: isFeature })}>
          {renderSkeleton ? (
            <Skeleton
              animation={'pulse'}
              variant="rectangular"
              width={'100%'}
              height={'50vh'}
            />
          ) : (
            <PostBody content={content} />
          )}
        </div>
        <footer className={headerClassName}>
          <Divider variant={'fullWidth'} className={classes.separator} />

          <Grid2
            container
            spacing={2}
            sx={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
            }}
          >
            {renderSkeleton ? (
              <Grid2 size={12}>
                <div style={{ display: 'flex', flexDirection: 'row' }}>
                  <Skeleton width={65} height={35} className={classes.skeleton} />
                  <Skeleton className={classes.skeleton} width={65} height={35} />
                  <Skeleton className={classes.skeleton} width={65} height={35} />
                </div>
              </Grid2>
            ) : (
              <Grid2 size={12}>{tags.length > 0 && <TagGrid tags={tags} />}</Grid2>
            )}
            <Grid2 size={12}>
              {credits && credits.length > 0 && <CreditGrid credits={credits} />}
            </Grid2>
          </Grid2>
        </footer>
      </article>
    </Paper>
  );
};

export interface PostPropTypes extends PostInfo {
  hasInstagramEmbed?: boolean;
  hasTwitterEmbed?: boolean;
  isLoading: boolean;
}

export default Post;
